import { EnvDir } from "./comEnvironmentFileSystem";
import { Client, ClientType } from "./comEnvironmentClient";

export abstract class PlatformDetector {
  private static client: Client = undefined;

  static isIOS(): boolean {
    return this.checkPlatform(ClientType.ios);
  }

  static isAndroid(): boolean {
    return this.checkPlatform(ClientType.android);
  }

  static isBrowser(): boolean {
    return this.checkPlatform(ClientType.browser);
  }

  private static checkPlatform(platform: ClientType): boolean {
    if (PlatformDetector.client === undefined) {
      PlatformDetector.client = new Client(null, null, null);
    }

    return PlatformDetector.client.type === platform;
  }
}

abstract class PathResolver {
  readonly PATH_TO_ASSETS = "www/assets/";
  abstract resolve(environmentDirectory: EnvDir): string;
}

class BrowserPathResolver extends PathResolver {
  resolve(environmentDirectory: EnvDir): string {
    let cleanedPathname = window.location.pathname.split("/")[1];
    return `/${cleanedPathname}/assets/`.replace("//", "/");
  }
}

class NoCordovaPathResolver extends PathResolver {
  resolve(environmentDirectory: EnvDir): string {
    return this.PATH_TO_ASSETS;
  }
}

class MobilePathResolver extends PathResolver {
  public resolve(environmentDirectory: EnvDir): string {
    switch (environmentDirectory) {
      case EnvDir.Assets:
        return this.findAssetFolder();
      case EnvDir.Data:
        return this.findDataFolder();
      case EnvDir.Cache:
        return this.findCacheFolder();
      default:
        throw new Error(
          this.constructor.name +
          ": Resolve accessed the default branch. Please validate EnvDir and its usages"
        );
    }
  }

  findAssetFolder(): string {
    return cordova.file.applicationDirectory + this.PATH_TO_ASSETS;
  }

  findDataFolder(): string {
    return cordova.file.dataDirectory;
  }

  findCacheFolder(): string {
    return cordova.file.cacheDirectory;
  }
}

export abstract class DirectoryPathProvider {
  private static readonly MOBILE = new MobilePathResolver();
  private static readonly BROWSER = new BrowserPathResolver();
  private static readonly WITHOUT_CORDOVA = new NoCordovaPathResolver();

/**
 * Provides the correct PathResolver for the current platform
 * 
 * @param bypass -There is a special case for Android! The asset directories will be fetched and catched for all assets.
 * BUT: The loading image and the error image are not listed in that cached data and must be statically available.
 * Therefore the path provision must be bypassed, because Android uses a WebServer for the app.
 * If bypassed the path will be resolved by the browser path resolver.
 * It is possible to create the same behaviour by using FileEntries created with window.resolveLocalFileSystemURL.
 * Using it would mean a drastic increase in complexity and a lot of additional code.
 */
  public static provide(forceBrowser : boolean): PathResolver {
    if (this.isCordovaAvailable()) {
      return !this.isFilePluginAvailable() || PlatformDetector.isBrowser()
        ? this.BROWSER
        : forceBrowser
          ? this.BROWSER
          : this.MOBILE;
    } else {
      return this.WITHOUT_CORDOVA;
    }
  }

  private static isCordovaAvailable(): boolean {
    return typeof cordova !== "undefined";
  }

  private static isFilePluginAvailable(): boolean {
    return typeof cordova.file !== "undefined";
  }
}
